'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

// ⌘K search over the docs pages: title, section and summary, all passed in by
// the server (about 40 entries), so it needs no index request and no service.

export type DocsSearchEntry = { href: string; title: string; section: string; summary: string };

type Labels = { placeholder: string; empty: string; open: string };

function score(entry: DocsSearchEntry, terms: string[]): number {
  const title = entry.title.toLowerCase();
  const rest = `${entry.section} ${entry.summary} ${entry.href}`.toLowerCase();
  let total = 0;
  for (const term of terms) {
    if (title.includes(term)) total += title.startsWith(term) ? 6 : 4;
    else if (rest.includes(term)) total += 1;
    else return 0;
  }
  return total;
}

export default function DocsSearch({ entries, labels }: { entries: DocsSearchEntry[]; labels: Labels }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return entries.slice(0, 8);
    return entries
      .map((entry) => ({ entry, s: score(entry, terms) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 8)
      .map((r) => r.entry);
  }, [entries, query]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const typing = event.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName);
      if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !typing)) {
        event.preventDefault();
        setOpen(true);
      } else if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setActive(0);
      window.setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  function go(href: string) {
    setOpen(false);
    window.location.href = href;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-full items-center gap-2 rounded-lg border border-black/[0.1] bg-white px-3 text-left text-[13.5px] text-[var(--text-muted)] transition-[border-color] duration-150 hover:border-black/[0.2] md:w-72"
        aria-label={labels.open}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <span className="flex-1 truncate">{labels.placeholder}</span>
        <kbd className="hidden rounded border border-black/[0.1] px-1.5 font-sans text-[11px] md:inline">⌘K</kbd>
      </button>
      {open && (
        <div className="enter-fade-rise fixed inset-0 z-[60] flex items-start justify-center bg-black/20 px-4 pt-[12vh]" onClick={() => setOpen(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={labels.open}
            className="w-full max-w-xl overflow-hidden rounded-xl border border-black/[0.08] bg-white shadow-[var(--glass-shadow)]"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              ref={input}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive((i) => Math.min(i + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter' && results[active]) {
                  go(results[active].href);
                }
              }}
              placeholder={labels.placeholder}
              className="h-12 w-full border-b border-black/[0.08] px-4 text-[15px] outline-none"
            />
            <ul className="max-h-[50vh] overflow-y-auto p-2" role="listbox">
              {results.length === 0 && <li className="px-3 py-6 text-center text-[14px] text-[var(--text-muted)]">{labels.empty}</li>}
              {results.map((entry, i) => (
                <li key={entry.href} role="option" aria-selected={i === active}>
                  <a
                    href={entry.href}
                    onMouseEnter={() => setActive(i)}
                    className={`block rounded-lg px-3 py-2.5 ${i === active ? 'bg-[var(--bg-muted)]' : ''}`}
                  >
                    <span className="block text-[12px] text-[var(--text-muted)]">{entry.section}</span>
                    <span className="block text-[14px] font-medium text-[var(--text-primary)]">{entry.title}</span>
                    <span className="mt-0.5 block truncate text-[13px] text-[var(--text-secondary)]">{entry.summary}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
